import { ParagraphScreen } from '@/features/advisor-onboarding/questionnaire/components/paragraph-screen';
import { ShortTextScreen } from '@/features/advisor-onboarding/questionnaire/components/short-text-screen';
import { MultiSelectScreen } from '@/features/advisor-onboarding/questionnaire/components/multi-select-screen';
import { MultiSelectWithPrimaryScreen } from '@/features/advisor-onboarding/questionnaire/components/multi-select-with-primary-screen';
import { SingleSelectScreen } from '@/features/advisor-onboarding/questionnaire/components/single-select-screen';
import { PartnerInterestScreen } from '@/features/advisor-onboarding/questionnaire/components/partner-interest-screen';
import { QuestionScreenProvider } from '@/features/advisor-onboarding/questionnaire/contexts/question-screen-context';

import { useQuestionSubmitHandlers } from '@/features/advisor-onboarding/questionnaire/hooks/use-question-submit-handlers';
import { useQuestionViewAnalytics } from '@/features/advisor-onboarding/questionnaire/hooks/use-question-view-analytics';
import { useResolvedQuestion } from '@/features/advisor-onboarding/questionnaire/hooks/use-resolved-question';

import type { OrgType, Question, QuestionnaireAnswers } from '@shared';

interface QuestionScreenProps {
  question: Question;
  answers: QuestionnaireAnswers;
  orgType: OrgType | null;
  organizationName: string | null;
  progressCurrent: number;
  progressEstimatedTotal: number;
  isSubmitting: boolean;
  onBack: () => void;
  onSubmit: (patch: Partial<QuestionnaireAnswers>) => Promise<void> | void;
  onAdvance: (patch?: Partial<QuestionnaireAnswers>) => void;
}

export function QuestionScreen({
  question,
  answers,
  orgType,
  organizationName,
  progressCurrent,
  progressEstimatedTotal,
  isSubmitting,
  onBack,
  onSubmit,
  onAdvance,
}: QuestionScreenProps) {
  useQuestionViewAnalytics(question.id);

  const resolved = useResolvedQuestion({ question, answers, orgType, organizationName });

  const { submitAnswer, submitAnswerWithoutAdvance, submitSkip, advance } =
    useQuestionSubmitHandlers({
      questionId: question.id,
      onSubmit,
      onAdvance,
    });

  return (
    <QuestionScreenProvider
      question={question}
      resolved={resolved}
      answers={answers}
      progress={{ current: progressCurrent, estimatedTotal: progressEstimatedTotal }}
      isSubmitting={isSubmitting}
      onBack={onBack}
      submitAnswer={submitAnswer}
      submitAnswerWithoutAdvance={submitAnswerWithoutAdvance}
      submitSkip={submitSkip}
      advance={advance}
    >
      {renderScreenForType(question)}
    </QuestionScreenProvider>
  );
}

function renderScreenForType(question: Question) {
  switch (question.type) {
    case 'single-select':
      return question.id === 'bga_partner_interest' ? (
        <PartnerInterestScreen />
      ) : (
        <SingleSelectScreen />
      );
    case 'multi-select':
      return <MultiSelectScreen />;
    case 'multi-select-with-primary':
      return <MultiSelectWithPrimaryScreen />;
    case 'short-text':
      return <ShortTextScreen />;
    case 'paragraph':
      return <ParagraphScreen />;
    default:
      return null;
  }
}
